import { useCallback, useEffect, useRef, useState } from 'react';
import Dropdown from '../../components/Dropdown';
import Field from '../../components/Field';
import Icon from '../../components/Icon';
import { ErrorBanner, Toast } from '../../components/Feedback';
import useForm from '../../hooks/useForm';
import * as codeRules from '../../validation/codeRules';
import { api } from '../../api/client';
import { sameId } from '../../ids';

/**
 * Sales view: hand a school the codes it has paid for.
 *
 * <p>The rep never picks individual codes — the server takes the oldest unused ones
 * for the title under a lock, so two reps allocating at the same moment cannot both
 * claim the same codes.
 */
export default function SalesCodesPage() {
  const [inventory, setInventory] = useState([]);
  const [school, setSchool] = useState(null);
  const [summary, setSummary] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const form = useForm({
    initialValues: { schoolId: '', titleId: '', quantity: '30' },
    validators: {
      schoolId: codeRules.schoolId,
      titleId: codeRules.titleId,
      quantity: codeRules.allocationQuantity,
    },
  });

  const loadInventory = useCallback(() => api.codeInventory().then(setInventory), []);

  useEffect(() => {
    loadInventory().catch(setError);
  }, [loadInventory]);

  // setValue, not setFieldValue: picking a school must not mark the field visited on
  // first render, or the form greets the rep with "School is required".
  const { setValue } = form;
  useEffect(() => {
    setValue('schoolId', school?.id ?? '');
    if (!school) {
      setSummary(null);
      return;
    }
    api.schoolCodes(school.id).then(setSummary).catch(setError);
  }, [school, setValue]);

  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const allocation = await api.allocateCodes({
        schoolId: values.schoolId,
        titleId: values.titleId,
        quantity: Number(values.quantity),
      });
      setResult(allocation);
      setToast(`${allocation.allocated} code(s) allocated to ${allocation.schoolName}`);
      await loadInventory();
      setSummary(await api.schoolCodes(values.schoolId));
    } catch (err) {
      if (!form.applyServerErrors(err, codeRules.SERVER_CODES)) setError(err);
    }
  });

  // form.values.titleId is the scalar the dropdown put in the form, which is a string
  // when it came back from a query string; row.titleId is a number off the API. sameId
  // compares them as the same id rather than as a number and a string, which never match.
  const selected = inventory.find((row) => sameId(row.titleId, form.values.titleId));

  return (
    <>
      <div className="page-head">
        <h1>Activation Codes</h1>
        <p>
          Allocate activation codes to one of your schools. A code stays with that
          school until a student redeems it.
        </p>
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      <section className="card card-pad" style={{ marginBottom: 'var(--s1)' }}>
        <h2 className="eyebrow" style={{ marginTop: 0 }}>Unallocated stock</h2>
        <div className="stat-row">
          {inventory.map((row) => (
            <div className="stat" key={row.titleId}>
              <b>{row.unallocated.toLocaleString()}</b>
              <span title={row.titleName}>{row.shortName}</span>
            </div>
          ))}
          {inventory.length === 0 && <p className="muted small">No titles in the catalogue yet.</p>}
        </div>
      </section>

      <section className="card card-pad" style={{ marginBottom: 'var(--s1)' }}>
        <form className="stack" {...form.formProps} onSubmit={submit}>
          <SchoolSelect value={school} onChange={setSchool} error={form.errorFor('schoolId')} />

          <div className="grid-2">
            <Field label="Title" id="titleId" required error={form.errorFor('titleId')}>
              <Dropdown
                id="titleId"
                required
                options={inventory.map((row) => ({
                  value: row.titleId,
                  label: `${row.titleName} (${row.unallocated.toLocaleString()} left)`,
                }))}
                value={form.values.titleId}
                onChange={(titleId) => form.setValue('titleId', titleId)}
                placeholder="Select a title"
                error={form.errorFor('titleId')}
              />
            </Field>

            <Field
              label="Quantity"
              id="quantity"
              required
              error={form.errorFor('quantity')}
              hint={
                selected
                  ? `${selected.unallocated.toLocaleString()} unallocated for this title`
                  : `Up to ${codeRules.LIMITS.allocation.toLocaleString()} at a time`
              }
            >
              <input className="input" inputMode="numeric" maxLength={4} {...form.field('quantity')} />
            </Field>
          </div>

          <div>
            <button type="submit" className="btn btn-primary" disabled={form.submitting}>
              {form.submitting ? 'Allocating…' : 'Allocate codes'}
            </button>
          </div>
        </form>
      </section>

      {summary && (
        <section className="card card-pad" style={{ marginBottom: 'var(--s1)' }}>
          <h2 className="eyebrow" style={{ marginTop: 0 }}>{summary.schoolName}</h2>
          <div className="stat-row" style={{ marginBottom: 'var(--s1)' }}>
            <div className="stat">
              <b>{summary.allocated.toLocaleString()}</b>
              <span>Allocated</span>
            </div>
            <div className="stat">
              <b>{summary.used.toLocaleString()}</b>
              <span>Used</span>
            </div>
            <div className="stat">
              <b>{summary.unused.toLocaleString()}</b>
              <span>Still unused</span>
            </div>
          </div>

          {summary.titles.length === 0 ? (
            <p className="muted small" style={{ margin: 0 }}>
              This school has no codes yet.
            </p>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Adopted</th>
                    <th>Allocated</th>
                    <th>Used</th>
                    <th>Unused</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.titles.map((row) => (
                    <tr key={row.titleId}>
                      <td>{row.titleName}</td>
                      <td>
                        {row.adopted ? (
                          <Icon name="check" size={16} />
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="mono">{row.allocated}</td>
                      <td className="mono">{row.used}</td>
                      <td className="mono">{row.unused}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {result && (
        <section className="card card-pad">
          <div className="row-between" style={{ marginBottom: 'var(--s1)' }}>
            <h2 className="eyebrow" style={{ margin: 0 }}>
              Just allocated · {result.titleName}
            </h2>
            <span className="muted small">
              {result.remainingUnallocated.toLocaleString()} left unallocated
            </span>
          </div>
          <ul className="code-grid">
            {result.codes.map((code) => (
              <li key={code.id} className="code-text">
                {code.code}
              </li>
            ))}
          </ul>
        </section>
      )}

      <Toast message={toast} onDone={() => setTimeout(() => setToast(null), 2600)} />
    </>
  );
}

/**
 * Typeahead over the caller's own schools. Deliberately not the public school lookup:
 * a rep can only allocate to schools in their own states, so offering the rest would
 * only produce a "not found" on submit.
 */
function SchoolSelect({ value, onChange, error }) {
  const [text, setText] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const blurTimer = useRef(null);

  useEffect(() => {
    if (value?.name && value.name !== text) setText(value.name);
    // Only re-sync when the selection itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.id]);

  useEffect(() => {
    if (value?.name && value.name === text) {
      setResults([]);
      return undefined;
    }
    const handle = setTimeout(() => {
      api
        .listSchools({ query: text.trim(), size: 10 })
        .then((page) => setResults(page.items))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(handle);
  }, [text, value?.name]);

  useEffect(() => () => clearTimeout(blurTimer.current), []);

  return (
    <Field label="School" id="schoolId" required error={error} hint="Only schools in your states">
      <input
        id="schoolId"
        name="schoolId"
        className="input"
        autoComplete="off"
        maxLength={200}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? 'schoolId-error' : undefined}
        value={text}
        placeholder="Start typing a school name or code"
        onFocus={() => setOpen(true)}
        onBlur={() => {
          blurTimer.current = setTimeout(() => setOpen(false), 150);
        }}
        onChange={(event) => {
          setText(event.target.value);
          if (value) onChange(null);
          setOpen(true);
        }}
      />
      {open && results.length > 0 && (
        <ul className="suggestions">
          {results.map((school) => (
            <li key={school.id}>
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onChange({ id: school.id, name: school.name });
                  setText(school.name);
                  setOpen(false);
                }}
              >
                {school.name}
                <span className="muted small"> · {school.code}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Field>
  );
}
